import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { forkJoin, Observable, of, lastValueFrom } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { ExchangeService } from 'projects/shared/services/exchange.service';
import { WebSettingService } from 'projects/shared/services/web-setting.service';
import { AuthService } from 'projects/shared/services/auth.service';
import { StoreChangesService } from 'projects/shared/services/store-changes.service';
import { IWebSetting } from 'projects/shared/models/interfaces/IWebSetting';
import { IUser } from 'projects/shared/models/interfaces/user.model';

export interface AppInitData {
  exchange: any;
  webSetting: IWebSetting | null;
  user: { success: boolean; response: IUser | null };
}

@Injectable({ providedIn: 'root' })
export class AppInitializerService {
  private _initData: AppInitData | null = null;
  private _initPromise: Promise<AppInitData> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private exchangeService: ExchangeService,
    private webSettingService: WebSettingService,
    private authService: AuthService,
    private storeChangesService: StoreChangesService
  ) { }

  get initData(): AppInitData | null {
    return this._initData;
  }

  async initialize(): Promise<AppInitData> {
    if (this._initPromise) {
      return this._initPromise;
    }
    if (this._initData) {
      return Promise.resolve(this._initData);
    }

    const isServer = isPlatformServer(this.platformId);
    console.log(`🚀 [APP_INITIALIZER] Starting... (${isServer ? 'SERVER' : 'BROWSER'})`);
    const startTime = Date.now();

    // ✅ مفيش أي منطق TransferState هنا خالص
    // الـ transferStateInterceptor بيتكفل بالتخزين/القراءة تلقائيًا
    // على مستوى كل HTTP request بيحصل جوه الخدمات دي
    this._initPromise = lastValueFrom(
      forkJoin({
        exchange: this.exchangeService.getUserExchange().pipe(
          timeout(5000),
          tap(() => console.log('✅ Exchange loaded')),
          catchError((err) => {
            console.warn('⚠️ Failed to load exchange:', err.message || err);
            return of(null);
          })
        ),
        webSetting: this.webSettingService.getWebsetting().pipe(
          timeout(5000),
          tap(() => console.log('✅ WebSetting loaded')),
          catchError((err) => {
            console.error('❌ Failed to load webSetting:', err.message || err);
            return of(null);
          })
        ),
        user: this.loadUser().pipe(
          timeout(5000),
          catchError(() => {
            console.log('ℹ️ User not authenticated (expected)');
            return of({ success: false, response: null });
          })
        ),
      }).pipe(
        tap((data) => {
          this._initData = data;

          try {
            if (data.exchange) {
              this.storeChangesService.setExchange(data.exchange);
            }
            if (data.webSetting) {
              this.storeChangesService.setWalletShow(data.webSetting.hide_wallet);
            }
            if (data.user.success && data.user.response) {
              this.storeChangesService.setUser(data.user.response);
              this.authService.updateUserShop(data.user.response);
            }
          } catch (storeError) {
            console.warn('⚠️ Store update error (non-critical):', storeError);
          }

          const duration = Date.now() - startTime;
          console.log(`✅ [APP_INITIALIZER] Completed in ${duration}ms`);
        }),
        catchError((err) => {
          console.error('❌ [APP_INITIALIZER] Critical error:', err);
          return of({
            exchange: null,
            webSetting: null,
            user: { success: false, response: null },
          } as AppInitData);
        })
      )
    ).catch((err) => {
      console.error('❌ [APP_INITIALIZER] Promise rejected:', err);
      return {
        exchange: null,
        webSetting: null,
        user: { success: false, response: null },
      } as AppInitData;
    });

    return this._initPromise;
  }
  private loadUser(): Observable<{ success: boolean; response: IUser | null }> {
    return this.authService.validate().pipe(
      map((response) => {
        console.log('✅ User validated');
        return { success: true, response };
      }),
      catchError((err) => {
        console.log('ℹ️ User not authenticated');
        return of({ success: false, response: null });
      })
    );
  }
}

export function appInitializerFactory(
  appInitializer: AppInitializerService
): () => Promise<AppInitData> {
  return () => appInitializer.initialize();
}

